import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { CATEGORIES, CATEGORY_MAP } from "../data/categories";
import { toast } from "sonner";
import Logo from "../components/Logo";
function resolveImage(url) {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api")) return `${BACKEND_URL}${url}`;
    return url;
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("menu"); // menu | settings
    const [authChecked, setAuthChecked] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem("admin_token")) {
            navigate("/admin/login", { replace: true });
            return;
        }
        api.get("/admin/me")
            .then(() => setAuthChecked(true))
            .catch(() => {
                localStorage.removeItem("admin_token");
                navigate("/admin/login", { replace: true });
            });
    }, [navigate]);

    const logout = () => {
        localStorage.removeItem("admin_token");
        navigate("/admin/login", { replace: true });
    };

    if (!authChecked) {
        return (
            <div className="py-20 text-center text-text-muted">
                <i className="fa-solid fa-spinner fa-spin text-2xl text-brand" />
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-6">
            <header className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Logo size={44} />
                    <div>
                        <div className="font-black text-text-main text-lg">لوحة الإدارة</div>
                        <div className="text-xs text-text-muted">تسالي سكة 10</div>
                    </div>
                </div>
                <button
                    onClick={logout}
                    data-testid="admin-logout"
                    className="bg-white border-2 border-[var(--border-subtle)] rounded-xl px-3 py-2 text-sm font-bold text-text-main hover:border-brand hover:text-brand transition"
                >
                    <i className="fa-solid fa-right-from-bracket ms-1" /> خروج
                </button>
            </header>

            <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
                <TabBtn id="menu" cur={tab} setCur={setTab} icon="fa-utensils" label="المنيو" />
                <TabBtn id="settings" cur={tab} setCur={setTab} icon="fa-gear" label="الإعدادات" />
            </div>

            {tab === "menu" && <MenuPanel />}
            {tab === "settings" && <SettingsPanel />}
        </div>
    );
}

export function TabBtn({ id, cur, setCur, icon, label }) {
    const active = cur === id;
    return (
        <button
            onClick={() => setCur(id)}
            data-testid={`tab-${id}`}
            className={`flex-1 whitespace-nowrap py-2.5 px-3 rounded-xl font-extrabold transition ${
                active ? "bg-brand text-white" : "bg-white border border-[var(--border-subtle)] text-text-main"
            }`}
        >
            <i className={`fa-solid ${icon} ms-1`} /> {label}
        </button>
    );
}

export function MenuPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // item or "new"
    const [form, setForm] = useState({
        name: "",
        category: "burgers",
        price: 0,
        meal_price: "",
        image: "",
        available: true,
        sort_order: 0,
    });
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/menu/items")
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("تعذر تحميل الأصناف"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setForm({ name: "", category: "burgers", price: 0, meal_price: "", image: "", available: true, sort_order: 0 });
        setEditing("new");
    };

    const openEdit = (it) => {
        setForm({
            name: it.name,
            category: it.category,
            price: it.price,
            meal_price: it.meal_price ?? "",
            image: it.image || "",
            available: it.available,
            sort_order: it.sort_order || 0,
        });
        setEditing(it);
    };

    const uploadImage = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("حجم الصورة أكبر من 5MB");
            return;
        }
        const data = new FormData();
        data.append("file", file);
        setUploading(true);
        try {
            const res = await api.post("/admin/upload-image", data, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, image: res.data.url }));
            toast.success("تم رفع الصورة");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "تعذر رفع الصورة");
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                meal_price: form.meal_price === "" ? null : Number(form.meal_price),
                sort_order: Number(form.sort_order) || 0,
            };
            if (editing === "new") {
                await api.post("/admin/menu/items", payload);
                toast.success("تمت الإضافة");
            } else {
                await api.put(`/admin/menu/items/${editing.id}`, payload);
                toast.success("تم الحفظ");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "تعذر الحفظ");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("تأكيد حذف هذا الصنف؟")) return;
        try {
            await api.delete(`/admin/menu/items/${id}`);
            toast.success("تم الحذف");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    return (
        <div data-testid="menu-panel">
            <div className="flex justify-end mb-3">
                <button onClick={openNew} data-testid="menu-add-new" className="btn-brand text-sm">
                    <i className="fa-solid fa-plus ms-1" /> إضافة صنف
                </button>
            </div>

            {loading && <div className="text-center text-text-muted py-8"><i className="fa-solid fa-spinner fa-spin text-brand text-2xl" /></div>}

            <div className="space-y-2">
                {items.map((it) => (
                    <div key={it.id} data-testid={`menu-item-${it.id}`} className="surface-card p-3 flex items-center justify-between gap-3">
                        <div
                            className="w-14 h-14 rounded-xl bg-[var(--bg)] bg-cover bg-center flex-shrink-0"
                            style={{ backgroundImage: it.image ? `url('${resolveImage(it.image)}')` : "none" }}
                        >
                            {!it.image && (
                                <div className="w-full h-full flex items-center justify-center text-text-muted">
                                    <i className="fa-solid fa-image" />
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-extrabold text-text-main truncate">
                                {it.name}
                                {!it.available && (
                                    <span className="ms-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                                        غير متاح
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-text-muted">
                                {CATEGORY_MAP[it.category]?.name} • {it.price} ر.س
                                {it.meal_price ? ` / وجبة ${it.meal_price} ر.س` : ""}
                            </div>
                        </div>
                        <button
                            onClick={() => openEdit(it)}
                            data-testid={`menu-edit-${it.id}`}
                            className="text-brand p-2 hover:bg-[var(--bg)] rounded-lg"
                        >
                            <i className="fa-solid fa-pen" />
                        </button>
                        <button
                            onClick={() => remove(it.id)}
                            data-testid={`menu-delete-${it.id}`}
                            className="text-red-600 p-2 hover:bg-red-50 rounded-lg"
                        >
                            <i className="fa-solid fa-trash" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setEditing(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="menu-edit-modal">
                        <h3 className="text-xl font-black text-text-main">
                            {editing === "new" ? "إضافة صنف جديد" : "تعديل الصنف"}
                        </h3>

                        {/* Image picker */}
                        <div>
                            <span className="text-sm font-bold block mb-1.5">صورة الصنف</span>
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-20 h-20 rounded-xl bg-[var(--bg)] border border-[var(--border-subtle)] bg-cover bg-center flex-shrink-0 flex items-center justify-center text-text-muted"
                                    style={{ backgroundImage: form.image ? `url('${resolveImage(form.image)}')` : "none" }}
                                >
                                    {!form.image && <i className="fa-solid fa-image text-2xl" />}
                                </div>
                                <div className="flex-1 space-y-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp,image/gif"
                                        className="hidden"
                                        data-testid="edit-image-file"
                                        onChange={(e) => uploadImage(e.target.files?.[0])}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="edit-image-upload"
                                        className="btn-outline-brand text-xs px-3 py-1.5 w-full disabled:opacity-50"
                                    >
                                        {uploading ? (
                                            <><i className="fa-solid fa-spinner fa-spin ms-1" /> جاري الرفع...</>
                                        ) : (
                                            <><i className="fa-solid fa-upload ms-1" /> رفع صورة</>
                                        )}
                                    </button>
                                    {form.image && (
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, image: "" })}
                                            data-testid="edit-image-remove"
                                            className="text-xs text-red-600 font-bold w-full text-center"
                                        >
                                            <i className="fa-solid fa-xmark ms-1" /> إزالة الصورة
                                        </button>
                                    )}
                                </div>
                            </div>
                            <input
                                type="text"
                                value={form.image}
                                onChange={(e) => setForm({ ...form, image: e.target.value })}
                                data-testid="edit-image-url"
                                placeholder="أو ألصق رابط صورة"
                                className="w-full mt-2 px-3 py-2 text-sm border-2 border-[var(--border-subtle)] rounded-xl"
                            />
                        </div>

                        <label className="block">
                            <span className="text-sm font-bold">الاسم</span>
                            <input
                                className="w-full mt-1 px-3 py-2 border-2 border-[var(--border-subtle)] rounded-xl"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="edit-name"
                            />
                        </label>
                        <label className="block">
                            <span className="text-sm font-bold">القسم</span>
                            <select
                                className="w-full mt-1 px-3 py-2 border-2 border-[var(--border-subtle)] rounded-xl bg-white"
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="edit-category"
                            >
                                {CATEGORIES.map((c) => (
                                    <option key={c.key} value={c.key}>{c.name}</option>
                                ))}
                            </select>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                                <span className="text-sm font-bold">السعر</span>
                                <input
                                    type="number"
                                    step="0.5"
                                    className="w-full mt-1 px-3 py-2 border-2 border-[var(--border-subtle)] rounded-xl"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    data-testid="edit-price"
                                />
                            </label>
                            <label className="block">
                                <span className="text-sm font-bold">سعر الوجبة (اختياري)</span>
                                <input
                                    type="number"
                                    step="0.5"
                                    className="w-full mt-1 px-3 py-2 border-2 border-[var(--border-subtle)] rounded-xl"
                                    value={form.meal_price}
                                    onChange={(e) => setForm({ ...form, meal_price: e.target.value })}
                                    data-testid="edit-meal-price"
                                />
                            </label>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.available}
                                onChange={(e) => setForm({ ...form, available: e.target.checked })}
                                data-testid="edit-available"
                            />
                            <span className="text-sm font-bold">متاح للطلب</span>
                        </label>
                        <div className="flex gap-2 pt-2">
                            <button onClick={save} data-testid="edit-save" className="btn-brand flex-1">
                                <i className="fa-solid fa-check ms-1" /> حفظ
                            </button>
                            <button
                                onClick={() => setEditing(null)}
                                data-testid="edit-cancel"
                                className="btn-outline-brand flex-1"
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SettingsPanel() {
    const [whatsapp, setWhatsapp] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/settings")
            .then((r) => {
                setWhatsapp(r.data?.whatsapp || "");
            })
            .catch(() => toast.error("تعذر تحميل الإعدادات"))
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        const digits = (whatsapp || "").replace(/\D/g, "");
        if (digits.length < 9) {
            toast.error("رقم الواتساب غير صحيح");
            return;
        }
        setSaving(true);
        try {
            const res = await api.put("/admin/settings", { whatsapp: digits });
            setWhatsapp(res.data?.whatsapp || digits);
            toast.success("تم حفظ الإعدادات");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center py-12 text-text-muted"><i className="fa-solid fa-spinner fa-spin text-brand text-2xl" /></div>;
    }

    return (
        <div data-testid="settings-panel" className="space-y-4 max-w-lg">
            <div className="surface-card p-5">
                <h3 className="font-extrabold text-text-main mb-1">
                    <i className="fa-brands fa-whatsapp text-[color:var(--whatsapp)] ms-1" /> رقم الواتساب للطلبات
                </h3>
                <p className="text-text-muted text-xs mb-3">
                    سيتم استخدام هذا الرقم عند إرسال تأكيد الطلب عبر الواتساب. اكتب الرقم بصيغة دولية بدون رمز + (مثال: 966509900776).
                </p>
                <input
                    type="tel"
                    dir="ltr"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    data-testid="settings-whatsapp"
                    placeholder="966509900776"
                    className="w-full px-4 py-3 bg-[var(--bg)] border-2 border-[var(--border-subtle)] rounded-xl font-bold focus:outline-none focus:border-brand"
                />
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="settings-save"
                    className="btn-brand mt-4 disabled:opacity-50"
                >
                    {saving ? (
                        <><i className="fa-solid fa-spinner fa-spin ms-1" /> جاري الحفظ...</>
                    ) : (
                        <><i className="fa-solid fa-check ms-1" /> حفظ</>
                    )}
                </button>
            </div>

            <div className="surface-card p-5 text-sm text-text-muted">
                <h3 className="font-extrabold text-text-main mb-2">
                    <i className="fa-solid fa-shield-halved text-brand ms-1" /> الأمان
                </h3>
                <ul className="space-y-1.5 list-disc ms-5">
                    <li>الأسعار محفوظة في السيرفر — لا يمكن للعميل التلاعب بأسعار الطلب.</li>
                    <li>الدخول للوحة المدير محمي بـ <strong>5 محاولات</strong> كل 15 دقيقة لكل IP.</li>
                    <li>لتغيير كلمة المرور: حدّث القيمة في ملف <code className="bg-[var(--bg)] px-1.5 py-0.5 rounded">backend/.env</code> ثم أعد تشغيل الباك-إند.</li>
                </ul>
            </div>
        </div>
    );
}
